import React, { useState } from 'react';
import { api } from '../services/api';

const CreateOfferModal = ({ isOpen, onClose, onCreate, isDarkMode }) => {
    const [formData, setFormData] = useState({
        code: '',
        description: '',
        discountType: 'PERCENTAGE', // or FLAT
        discountValue: 0,
        minOrderValue: 0,
        maxDiscount: 0,
        usageLimit: 0,
        isActive: true
    });
    const [loading, setLoading] = useState(false);

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post('/restaurant/coupons/', {
                ...formData,
                discountValue: parseFloat(formData.discountValue),
                minOrderValue: parseFloat(formData.minOrderValue),
                maxDiscount: parseFloat(formData.maxDiscount),
                usageLimit: parseInt(formData.usageLimit)
            });
            onCreate(); // Refresh list
            onClose();
        } catch (error) {
            console.error('Failed to create coupon', error);
            alert('Failed to create coupon: ' + (error.response?.data?.error || error.message));
        } finally {
            setLoading(false);
        }
    };

    const styles = getStyles(isDarkMode);

    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>
                <div style={styles.header}>
                    <h3 style={styles.title}>Create New Offer</h3>
                    <button style={styles.closeBtn} onClick={onClose}>&times;</button>
                </div>
                <form onSubmit={handleSubmit} style={styles.form}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Coupon Code</label>
                        <input
                            type="text"
                            name="code"
                            value={formData.code}
                            onChange={handleChange}
                            style={styles.input}
                            placeholder="e.g. SAVE20"
                            required
                        />
                    </div>

                    <div style={styles.row}>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Discount Type</label>
                            <select
                                name="discountType"
                                value={formData.discountType}
                                onChange={handleChange}
                                style={styles.input}
                            >
                                <option value="PERCENTAGE">Percentage (%)</option>
                                <option value="FLAT">Flat Amount (₹)</option>
                            </select>
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Value</label>
                            <input
                                type="number"
                                name="discountValue"
                                value={formData.discountValue}
                                onChange={handleChange}
                                style={styles.input}
                                min="0"
                                required
                            />
                        </div>
                    </div>

                    <div style={styles.row}>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Min Order Value</label>
                            <input
                                type="number"
                                name="minOrderValue"
                                value={formData.minOrderValue}
                                onChange={handleChange}
                                style={styles.input}
                                min="0"
                            />
                        </div>
                        {formData.discountType === 'PERCENTAGE' && (
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Max Discount</label>
                                <input
                                    type="number"
                                    name="maxDiscount"
                                    value={formData.maxDiscount}
                                    onChange={handleChange}
                                    style={styles.input}
                                    min="0"
                                />
                            </div>
                        )}
                    </div>

                    <div style={styles.row}>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Usage Limit (0 for unlimited)</label>
                            <input
                                type="number"
                                name="usageLimit"
                                value={formData.usageLimit}
                                onChange={handleChange}
                                style={styles.input}
                                min="0"
                            />
                        </div>
                        <div style={{ ...styles.formGroup, justifyContent: 'center' }}>
                            <label style={{ ...styles.label, display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    name="isActive"
                                    checked={formData.isActive}
                                    onChange={handleChange}
                                    style={{ marginRight: '10px', width: 'auto' }}
                                />
                                Is Active
                            </label>
                        </div>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Description</label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            style={{ ...styles.input, height: '80px', resize: 'none' }}
                            placeholder="Optional description"
                        />
                    </div>

                    <div style={styles.footer}>
                        <button type="button" onClick={onClose} style={styles.cancelBtn}>Cancel</button>
                        <button type="submit" disabled={loading} style={styles.submitBtn}>
                            {loading ? 'Creating...' : 'Create Coupon'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const getStyles = (isDark) => ({
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    modal: {
        backgroundColor: isDark ? '#1E1E24' : '#fff',
        borderRadius: '12px',
        width: '500px',
        maxWidth: '90%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
        color: isDark ? '#fff' : '#333'
    },
    header: {
        padding: '20px',
        borderBottom: isDark ? '1px solid #333' : '1px solid #eee',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    title: {
        margin: 0,
        fontSize: '1.25rem'
    },
    closeBtn: {
        background: 'none',
        border: 'none',
        fontSize: '1.5rem',
        cursor: 'pointer',
        color: isDark ? '#aaa' : '#666'
    },
    form: {
        padding: '20px'
    },
    formGroup: {
        marginBottom: '16px',
        flex: 1
    },
    row: {
        display: 'flex',
        gap: '16px'
    },
    label: {
        display: 'block',
        marginBottom: '8px',
        fontWeight: '500',
        fontSize: '0.9rem'
    },
    input: {
        width: '100%',
        padding: '10px',
        borderRadius: '6px',
        border: isDark ? '1px solid #444' : '1px solid #ddd',
        backgroundColor: isDark ? '#2D3436' : '#fff',
        color: isDark ? '#fff' : '#333',
        fontSize: '0.95rem',
        boxSizing: 'border-box'
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '20px'
    },
    cancelBtn: {
        padding: '10px 20px',
        borderRadius: '6px',
        border: 'none',
        backgroundColor: isDark ? '#333' : '#f0f0f0',
        color: isDark ? '#ccc' : '#333',
        cursor: 'pointer',
        fontWeight: '500'
    },
    submitBtn: {
        padding: '10px 20px',
        borderRadius: '6px',
        border: 'none',
        backgroundColor: '#0984E3',
        color: '#fff',
        cursor: 'pointer',
        fontWeight: '500'
    }
});

export default CreateOfferModal;
